import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
    FolderKanban,
    Users,
    Zap,
    MessageSquare,
    LayoutGrid,
    Palette,
    Bell,
    Settings2,
    ChevronDown,
    Check,
    Sparkles,
    ArrowRight,
    Circle,
    Loader2,
    CheckCircle2,
    ArrowUp,
    ArrowDown,
    Minus,
    Calendar,
    Paperclip,
    Heart,
    Globe,
    Gift,
    Cloud,
    X,
    Maximize2,
    ChevronRight,
    GripVertical,
    ListTree,
    FileText,
    Image,
    File,
    User,
    Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useI18n, languageOptions } from '@/lib/i18n';
import { useThemeStore, themes, type Theme } from '@/stores/theme-store';
import { cn } from '@/lib/utils';

// Demo subtask data
interface DemoSubtask {
    id: string;
    title: string;
    titleFr: string;
    status: 'TODO' | 'IN_PROGRESS' | 'DONE';
}

// Demo comment data
interface DemoComment {
    id: string;
    author: string;
    authorColor: string;
    content: string;
    contentFr: string;
    createdAt: string;
}

// Demo attachment data
interface DemoAttachment {
    id: string;
    name: string;
    type: string;
    size: string;
}

// Demo task data
interface DemoTask {
    id: string;
    title: string;
    titleFr: string;
    description?: string;
    descriptionFr?: string;
    status: 'TODO' | 'IN_PROGRESS' | 'DONE';
    priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'NONE';
    assignee?: string;
    assigneeColor?: string;
    dueDate?: string;
    comments: number;
    attachments: number;
    subtaskCount?: number;
    subtasks?: DemoSubtask[];
    commentsList?: DemoComment[];
    attachmentsList?: DemoAttachment[];
}

const demoTasks: DemoTask[] = [
    {
        id: '1',
        title: 'Design new landing page',
        titleFr: 'Concevoir la nouvelle page d\'accueil',
        description: 'Create a modern, interactive landing page with theme selector, feature showcase, and demo section.',
        descriptionFr: 'Créer une page d\'accueil moderne et interactive avec sélecteur de thème, présentation des fonctionnalités et section démo.',
        status: 'DONE',
        priority: 'HIGH',
        assignee: 'Alice',
        assigneeColor: 'from-pink-500 to-rose-500',
        dueDate: '2026-02-03',
        comments: 5,
        attachments: 2,
        subtaskCount: 3,
        subtasks: [
            { id: '1-1', title: 'Create wireframes', titleFr: 'Créer les maquettes', status: 'DONE' },
            { id: '1-2', title: 'Implement hero section', titleFr: 'Implémenter la section hero', status: 'DONE' },
            { id: '1-3', title: 'Add animations', titleFr: 'Ajouter les animations', status: 'DONE' },
        ],
        commentsList: [
            { id: 'c1', author: 'Bob', authorColor: 'from-blue-500 to-cyan-500', content: 'Great progress on the design!', contentFr: 'Super progrès sur le design !', createdAt: '2 hours ago' },
            { id: 'c2', author: 'Charlie', authorColor: 'from-green-500 to-emerald-500', content: 'Love the gradient effects', contentFr: 'J\'adore les effets de dégradé', createdAt: '1 hour ago' },
        ],
        attachmentsList: [
            { id: 'a1', name: 'landing-mockup.fig', type: 'figma', size: '2.4 MB' },
            { id: 'a2', name: 'brand-colors.pdf', type: 'pdf', size: '156 KB' },
        ],
    },
    {
        id: '2',
        title: 'Implement user authentication',
        titleFr: 'Implémenter l\'authentification',
        description: 'Set up OAuth, JWT tokens, and session management.',
        descriptionFr: 'Configurer OAuth, les tokens JWT et la gestion des sessions.',
        status: 'IN_PROGRESS',
        priority: 'HIGH',
        assignee: 'Bob',
        assigneeColor: 'from-blue-500 to-cyan-500',
        dueDate: '2026-02-05',
        comments: 3,
        attachments: 1,
        subtaskCount: 2,
        subtasks: [
            { id: '2-1', title: 'Set up OAuth providers', titleFr: 'Configurer les fournisseurs OAuth', status: 'DONE' },
            { id: '2-2', title: 'Implement JWT refresh', titleFr: 'Implémenter le refresh JWT', status: 'IN_PROGRESS' },
        ],
        commentsList: [
            { id: 'c3', author: 'Alice', authorColor: 'from-pink-500 to-rose-500', content: 'Don\'t forget rate limiting', contentFr: 'N\'oublie pas le rate limiting', createdAt: '3 hours ago' },
        ],
        attachmentsList: [
            { id: 'a3', name: 'auth-flow.png', type: 'image', size: '340 KB' },
        ],
    },
    { id: '3', title: 'Write API documentation', titleFr: 'Rédiger la documentation API', status: 'TODO', priority: 'MEDIUM', assignee: 'Charlie', assigneeColor: 'from-green-500 to-emerald-500', dueDate: '2026-02-07', comments: 0, attachments: 0 },
    { id: '4', title: 'Set up CI/CD pipeline', titleFr: 'Configurer le pipeline CI/CD', status: 'TODO', priority: 'LOW', assignee: 'Alice', assigneeColor: 'from-pink-500 to-rose-500', dueDate: '2026-02-10', comments: 2, attachments: 1 },
    {
        id: '5',
        title: 'Create mobile responsive styles',
        titleFr: 'Créer les styles responsive mobile',
        description: 'Ensure all pages work well on mobile devices.',
        descriptionFr: 'S\'assurer que toutes les pages fonctionnent bien sur mobile.',
        status: 'IN_PROGRESS',
        priority: 'MEDIUM',
        assignee: 'Bob',
        assigneeColor: 'from-blue-500 to-cyan-500',
        comments: 1,
        attachments: 0,
        subtaskCount: 4,
        subtasks: [
            { id: '5-1', title: 'Dashboard mobile layout', titleFr: 'Layout mobile dashboard', status: 'DONE' },
            { id: '5-2', title: 'Task panel mobile view', titleFr: 'Vue mobile panneau tâches', status: 'IN_PROGRESS' },
            { id: '5-3', title: 'Navigation drawer', titleFr: 'Tiroir de navigation', status: 'TODO' },
            { id: '5-4', title: 'Touch gestures', titleFr: 'Gestes tactiles', status: 'TODO' },
        ],
    },
    { id: '6', title: 'Database optimization', titleFr: 'Optimisation de la base de données', status: 'TODO', priority: 'HIGH', assignee: 'Charlie', assigneeColor: 'from-green-500 to-emerald-500', dueDate: '2026-02-08', comments: 0, attachments: 0 },
];

// Status configuration
const statusConfig = {
    TODO: { label: 'To Do', labelFr: 'À faire', icon: Circle, color: 'text-gray-400', bg: 'bg-gray-500/20' },
    IN_PROGRESS: { label: 'In Progress', labelFr: 'En cours', icon: Loader2, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    DONE: { label: 'Done', labelFr: 'Terminé', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/20' },
};

const priorityConfig = {
    NONE: { label: 'None', labelFr: 'Aucune', icon: Minus, color: 'text-gray-400' },
    LOW: { label: 'Low', labelFr: 'Basse', icon: ArrowDown, color: 'text-gray-500' },
    MEDIUM: { label: 'Medium', labelFr: 'Moyenne', icon: ArrowUp, color: 'text-yellow-500' },
    HIGH: { label: 'High', labelFr: 'Haute', icon: ArrowUp, color: 'text-red-500' },
};

// Animated counter component
function AnimatedCounter({ target, duration = 2000 }: { target: number; duration?: number }) {
    const [count, setCount] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    let start = 0;
                    const step = target / (duration / 16);
                    const timer = setInterval(() => {
                        start += step;
                        if (start >= target) {
                            setCount(target);
                            clearInterval(timer);
                        } else {
                            setCount(Math.floor(start));
                        }
                    }, 16);
                }
            },
            { threshold: 0.5 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [target, duration, hasAnimated]);

    return <span ref={ref}>{count}</span>;
}

// Feature card component
function FeatureCard({
    icon: Icon,
    title,
    description,
    delay,
    gradient,
}: {
    icon: React.ElementType;
    title: string;
    description: string;
    delay: number;
    gradient: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay }}
            whileHover={{ scale: 1.02, y: -5 }}
            className="group relative"
        >
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"
                style={{ background: gradient }} />
            <div className="relative backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-6 h-full hover:border-white/20 transition-all duration-300">
                <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110",
                    "bg-gradient-to-br",
                    gradient
                )}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{description}</p>
            </div>
        </motion.div>
    );
}

// Interactive theme preview card
function ThemePreviewCard({
    themeConfig,
    isSelected,
    onClick,
    language,
}: {
    themeConfig: typeof themes[0];
    isSelected: boolean;
    onClick: () => void;
    language: 'en' | 'fr';
}) {
    const name = language === 'fr' ? themeConfig.nameFr : themeConfig.nameEn;

    return (
        <motion.button
            onClick={onClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className={cn(
                "relative group flex flex-col rounded-xl overflow-hidden border-2 transition-all duration-300 w-full",
                isSelected
                    ? 'border-primary ring-2 ring-primary/30 shadow-lg shadow-primary/20'
                    : 'border-white/10 hover:border-white/30'
            )}
        >
            <div
                className="h-16 w-full relative"
                style={{ background: themeConfig.preview }}
            >
                {themeConfig.interactive && (
                    <div className="absolute top-1.5 right-1.5">
                        <Sparkles className="h-3 w-3 text-white/80" />
                    </div>
                )}
                <AnimatePresence>
                    {isSelected && (
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center bg-black/40"
                        >
                            <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                                <Check className="h-4 w-4 text-white" />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            <div className="p-2 backdrop-blur-md bg-white/5 text-left">
                <p className="font-medium text-xs text-white truncate">{name}</p>
            </div>
        </motion.button>
    );
}

// Get file icon based on type
function getFileIcon(type: string) {
    switch (type) {
        case 'image': return Image;
        case 'pdf': return FileText;
        case 'figma': return Palette;
        default: return File;
    }
}

// Demo Task Detail Panel - mimics the real task-detail-panel.tsx
function DemoTaskDetailPanel({
    task,
    language,
    onClose,
    onStatusChange,
    onSubtaskStatusChange,
}: {
    task: DemoTask;
    language: 'en' | 'fr';
    onClose: () => void;
    onStatusChange: (status: DemoTask['status']) => void;
    onSubtaskStatusChange: (subtaskId: string, status: DemoTask['status']) => void;
}) {
    const [showSubtasks, setShowSubtasks] = useState(true);
    const [showComments, setShowComments] = useState(true);
    const [showAttachments, setShowAttachments] = useState(true);

    const status = statusConfig[task.status];
    const priority = priorityConfig[task.priority];
    const StatusIcon = status.icon;
    const PriorityIcon = priority.icon;

    const title = language === 'fr' ? task.titleFr : task.title;
    const description = language === 'fr' ? task.descriptionFr : task.description;

    return (
        <>
            {/* Backdrop - only closes the detail panel, not the parent modal */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
            />

            {/* Slide-out Panel */}
            <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="fixed inset-y-0 right-0 w-full sm:w-[480px] sm:max-w-[90vw] bg-surface/95 backdrop-blur-xl sm:border-l border-white/10 z-[70] flex flex-col shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0 bg-white/5">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <StatusIcon className={`h-5 w-5 shrink-0 ${status.color}`} />
                        <span className="text-sm font-medium text-white/70">
                            {language === 'fr' ? 'Détails de la tâche' : 'Task Details'}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X className="h-4 w-4 text-white/60" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 space-y-6">
                        {/* Title & Description */}
                        <div>
                            <h2 className="text-lg font-semibold text-white">{title}</h2>
                            {description && (
                                <p className="mt-2 text-sm text-white/60">{description}</p>
                            )}
                        </div>

                        {/* Quick Actions - Status & Priority */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Status Selector */}
                            <div className="space-y-1.5">
                                <label className="text-xs text-white/50 font-medium">
                                    {language === 'fr' ? 'Statut' : 'Status'}
                                </label>
                                <div className="relative">
                                    <button
                                        onClick={() => {
                                            const statuses: DemoTask['status'][] = ['TODO', 'IN_PROGRESS', 'DONE'];
                                            const currentIndex = statuses.indexOf(task.status);
                                            const nextStatus = statuses[(currentIndex + 1) % statuses.length] as DemoTask['status'];
                                            onStatusChange(nextStatus);
                                        }}
                                        className={cn(
                                            "w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/20 transition-colors",
                                            status.bg
                                        )}
                                    >
                                        <StatusIcon className={cn("h-4 w-4", status.color, task.status === 'IN_PROGRESS' && "animate-spin")} />
                                        <span className={cn("text-sm", status.color)}>
                                            {language === 'fr' ? status.labelFr : status.label}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* Priority Display */}
                            <div className="space-y-1.5">
                                <label className="text-xs text-white/50 font-medium">
                                    {language === 'fr' ? 'Priorité' : 'Priority'}
                                </label>
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5">
                                    <PriorityIcon className={cn("h-4 w-4", priority.color)} />
                                    <span className={cn("text-sm", priority.color)}>
                                        {language === 'fr' ? priority.labelFr : priority.label}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Meta Info */}
                        <div className="space-y-3">
                            {/* Due date */}
                            {task.dueDate && (
                                <div className="flex items-center gap-3 text-sm">
                                    <Calendar className="h-4 w-4 text-white/40" />
                                    <span className="text-white/60">
                                        {language === 'fr' ? 'Échéance' : 'Due'}: {' '}
                                        {new Date(task.dueDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                        })}
                                    </span>
                                </div>
                            )}

                            {/* Assignee */}
                            {task.assignee && (
                                <div className="flex items-center gap-3 text-sm">
                                    <User className="h-4 w-4 text-white/40" />
                                    <div className="flex items-center gap-2">
                                        <div className={cn(
                                            "w-6 h-6 rounded-full bg-gradient-to-br flex items-center justify-center text-[10px] font-medium text-white",
                                            task.assigneeColor || "from-primary/50 to-accent/50"
                                        )}>
                                            {task.assignee[0]}
                                        </div>
                                        <span className="text-white/60">{task.assignee}</span>
                                    </div>
                                </div>
                            )}

                            {/* Created at (fake) */}
                            <div className="flex items-center gap-3 text-sm">
                                <Clock className="h-4 w-4 text-white/40" />
                                <span className="text-white/60">
                                    {language === 'fr' ? 'Créé le 28 jan 2026' : 'Created Jan 28, 2026'}
                                </span>
                            </div>
                        </div>

                        {/* Subtasks Section */}
                        {task.subtasks && task.subtasks.length > 0 && (
                            <div className="space-y-2">
                                <button
                                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors w-full"
                                    onClick={() => setShowSubtasks(!showSubtasks)}
                                >
                                    {showSubtasks ? (
                                        <ChevronDown className="h-4 w-4" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                    <ListTree className="h-4 w-4" />
                                    <span className="font-medium text-sm">
                                        {language === 'fr' ? 'Sous-tâches' : 'Subtasks'} ({task.subtasks.length})
                                    </span>
                                </button>
                                <AnimatePresence>
                                    {showSubtasks && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden pl-2 space-y-1"
                                        >
                                            {task.subtasks.map((subtask) => {
                                                const subtaskStatus = statusConfig[subtask.status];
                                                const SubtaskStatusIcon = subtaskStatus.icon;
                                                return (
                                                    <motion.div
                                                        key={subtask.id}
                                                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                                                        onClick={() => {
                                                            const statuses: DemoTask['status'][] = ['TODO', 'IN_PROGRESS', 'DONE'];
                                                            const currentIndex = statuses.indexOf(subtask.status);
                                                            const nextStatus = statuses[(currentIndex + 1) % statuses.length] as DemoTask['status'];
                                                            onSubtaskStatusChange(subtask.id, nextStatus);
                                                        }}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        <SubtaskStatusIcon className={cn("h-4 w-4", subtaskStatus.color, subtask.status === 'IN_PROGRESS' && "animate-spin")} />
                                                        <span className={cn(
                                                            "text-sm flex-1",
                                                            subtask.status === 'DONE' ? "text-white/40 line-through" : "text-white/80"
                                                        )}>
                                                            {language === 'fr' ? subtask.titleFr : subtask.title}
                                                        </span>
                                                    </motion.div>
                                                );
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Comments Section */}
                        {task.commentsList && task.commentsList.length > 0 && (
                            <div className="space-y-2">
                                <button
                                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors w-full"
                                    onClick={() => setShowComments(!showComments)}
                                >
                                    {showComments ? (
                                        <ChevronDown className="h-4 w-4" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                    <MessageSquare className="h-4 w-4" />
                                    <span className="font-medium text-sm">
                                        {language === 'fr' ? 'Commentaires' : 'Comments'} ({task.commentsList.length})
                                    </span>
                                </button>
                                <AnimatePresence>
                                    {showComments && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden pl-2 space-y-3"
                                        >
                                            {task.commentsList.map((comment) => (
                                                <div key={comment.id} className="flex gap-3 px-3 py-2 rounded-lg bg-white/5">
                                                    <div className={cn(
                                                        "w-7 h-7 rounded-full bg-gradient-to-br flex items-center justify-center text-[10px] font-medium text-white shrink-0",
                                                        comment.authorColor
                                                    )}>
                                                        {comment.author[0]}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-white">{comment.author}</span>
                                                            <span className="text-[10px] text-white/40">{comment.createdAt}</span>
                                                        </div>
                                                        <p className="text-sm text-white/60 mt-0.5">
                                                            {language === 'fr' ? comment.contentFr : comment.content}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Attachments Section */}
                        {task.attachmentsList && task.attachmentsList.length > 0 && (
                            <div className="space-y-2">
                                <button
                                    className="flex items-center gap-2 text-white/70 hover:text-white transition-colors w-full"
                                    onClick={() => setShowAttachments(!showAttachments)}
                                >
                                    {showAttachments ? (
                                        <ChevronDown className="h-4 w-4" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                    <Paperclip className="h-4 w-4" />
                                    <span className="font-medium text-sm">
                                        {language === 'fr' ? 'Pièces jointes' : 'Attachments'} ({task.attachmentsList.length})
                                    </span>
                                </button>
                                <AnimatePresence>
                                    {showAttachments && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden pl-2 space-y-2"
                                        >
                                            {task.attachmentsList.map((attachment) => {
                                                const FileIcon = getFileIcon(attachment.type);
                                                return (
                                                    <div
                                                        key={attachment.id}
                                                        className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                                                    >
                                                        <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                                                            <FileIcon className="h-4 w-4 text-primary" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-white truncate">{attachment.name}</p>
                                                            <p className="text-[10px] text-white/40">{attachment.size}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-white/10 bg-white/5">
                    <p className="text-[10px] text-white/40 text-center">
                        {language === 'fr'
                            ? "💡 Cliquez sur le statut ou les sous-tâches pour les modifier"
                            : "💡 Click on status or subtasks to toggle them"}
                    </p>
                </div>
            </motion.div>
        </>
    );
}

// Interactive demo task row - more realistic like actual task table
function DemoTaskRow({
    task,
    language,
    onStatusChange,
    onSubtaskStatusChange,
    isSelected,
    onSelect,
    isExpanded,
    showSubtasks,
}: {
    task: DemoTask;
    language: 'en' | 'fr';
    onStatusChange: (id: string, status: DemoTask['status']) => void;
    onSubtaskStatusChange?: (taskId: string, subtaskId: string, status: DemoTask['status']) => void;
    isSelected: boolean;
    onSelect: () => void;
    isExpanded?: boolean;
    showSubtasks?: boolean;
}) {
    const status = statusConfig[task.status];
    const priority = priorityConfig[task.priority];
    const StatusIcon = status.icon;
    const PriorityIcon = priority.icon;

    const title = language === 'fr' ? task.titleFr : task.title;
    const statusLabel = language === 'fr' ? status.labelFr : status.label;
    const priorityLabel = language === 'fr' ? priority.labelFr : priority.label;

    const cycleStatus = () => {
        const statuses: DemoTask['status'][] = ['TODO', 'IN_PROGRESS', 'DONE'];
        const currentIndex = statuses.indexOf(task.status);
        const nextStatus = statuses[(currentIndex + 1) % statuses.length] as DemoTask['status'];
        onStatusChange(task.id, nextStatus);
    };

    return (
        <>
            <motion.div
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={cn(
                    "group border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer",
                    isSelected && "bg-primary/10"
                )}
                onClick={onSelect}
            >
                {/* Main row with proper grid alignment */}
                <div className={cn(
                    "grid gap-3 px-3 py-2.5 items-center",
                    isExpanded
                        ? "grid-cols-[24px_1fr_100px_80px_90px_90px_80px]"
                        : "grid-cols-[24px_1fr_auto_auto_auto]"
                )}>
                    {/* Checkbox */}
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={(e) => { e.stopPropagation(); cycleStatus(); }}
                        className={cn(
                            "flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                            task.status === 'DONE'
                                ? "bg-green-500 border-green-500"
                                : "border-white/30 hover:border-white/50"
                        )}
                    >
                        {task.status === 'DONE' && <Check className="h-3 w-3 text-white" />}
                    </motion.button>

                    {/* Task title with subtask indicator */}
                    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                        {task.subtaskCount && task.subtaskCount > 0 && (
                            <ChevronDown className="h-3.5 w-3.5 text-white/40 shrink-0" />
                        )}
                        <span className={cn(
                            "text-sm truncate transition-all",
                            task.status === 'DONE' ? "text-white/40 line-through" : "text-white"
                        )}>
                            {title}
                        </span>
                        {task.subtaskCount && task.subtaskCount > 0 && (
                            <span className="text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded shrink-0">
                                {task.subtaskCount}
                            </span>
                        )}
                    </div>

                    {/* Status badge */}
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => { e.stopPropagation(); cycleStatus(); }}
                        className={cn(
                            "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs whitespace-nowrap justify-center",
                            status.bg, status.color
                        )}
                    >
                        <StatusIcon className={cn("h-3 w-3", task.status === 'IN_PROGRESS' && "animate-spin")} />
                        <span className={isExpanded ? "" : "hidden sm:inline"}>{statusLabel}</span>
                    </motion.button>

                    {/* Priority */}
                    <div className={cn("flex items-center gap-1 text-xs whitespace-nowrap justify-center", priority.color)}>
                        <PriorityIcon className="h-3.5 w-3.5" />
                        {isExpanded && <span>{priorityLabel}</span>}
                    </div>

                    {/* Assignee */}
                    <div className="flex items-center gap-1.5 justify-center">
                        {task.assignee && (
                            <>
                                <div className={cn(
                                    "w-6 h-6 rounded-full bg-gradient-to-br flex items-center justify-center text-[10px] font-medium text-white",
                                    task.assigneeColor || "from-primary/50 to-accent/50"
                                )}>
                                    {task.assignee[0]}
                                </div>
                                {isExpanded && <span className="text-xs text-white/60">{task.assignee}</span>}
                            </>
                        )}
                    </div>

                    {/* Due date - in expanded mode */}
                    {isExpanded && (
                        <div className="flex items-center gap-1 text-white/40 text-xs whitespace-nowrap justify-center">
                            {task.dueDate ? (
                                <>
                                    <Calendar className="h-3 w-3" />
                                    <span>{new Date(task.dueDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric' })}</span>
                                </>
                            ) : <span className="text-white/20">-</span>}
                        </div>
                    )}

                    {/* Comments & Attachments - in expanded mode */}
                    {isExpanded && (
                        <div className="flex items-center gap-3 text-white/30 justify-center">
                            {task.comments > 0 && (
                                <div className="flex items-center gap-1 text-xs">
                                    <MessageSquare className="h-3 w-3" />
                                    <span>{task.comments}</span>
                                </div>
                            )}
                            {task.attachments > 0 && (
                                <div className="flex items-center gap-1 text-xs">
                                    <Paperclip className="h-3 w-3" />
                                    <span>{task.attachments}</span>
                                </div>
                            )}
                            {task.comments === 0 && task.attachments === 0 && (
                                <span className="text-white/20">-</span>
                            )}
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Subtasks - shown in expanded mode */}
            {isExpanded && showSubtasks && task.subtasks && task.subtasks.length > 0 && (
                <AnimatePresence>
                    {task.subtasks.map((subtask) => {
                        const subtaskStatus = statusConfig[subtask.status];
                        const SubtaskStatusIcon = subtaskStatus.icon;
                        return (
                            <motion.div
                                key={subtask.id}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="border-b border-white/5 bg-white/[0.02] hover:bg-white/5 transition-colors"
                            >
                                <div className="grid grid-cols-[24px_1fr_100px_80px_90px_90px_80px] gap-3 px-3 py-2 items-center pl-8">
                                    {/* Subtask checkbox */}
                                    <motion.button
                                        whileTap={{ scale: 0.9 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onSubtaskStatusChange) {
                                                const statuses: DemoTask['status'][] = ['TODO', 'IN_PROGRESS', 'DONE'];
                                                const currentIndex = statuses.indexOf(subtask.status);
                                                const nextStatus = statuses[(currentIndex + 1) % statuses.length] as DemoTask['status'];
                                                onSubtaskStatusChange(task.id, subtask.id, nextStatus);
                                            }
                                        }}
                                        className={cn(
                                            "flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all",
                                            subtask.status === 'DONE'
                                                ? "bg-green-500 border-green-500"
                                                : "border-white/20 hover:border-white/40"
                                        )}
                                    >
                                        {subtask.status === 'DONE' && <Check className="h-2.5 w-2.5 text-white" />}
                                    </motion.button>

                                    {/* Subtask title */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className={cn(
                                            "text-xs truncate transition-all",
                                            subtask.status === 'DONE' ? "text-white/30 line-through" : "text-white/60"
                                        )}>
                                            {language === 'fr' ? subtask.titleFr : subtask.title}
                                        </span>
                                    </div>

                                    {/* Subtask status */}
                                    <div className={cn(
                                        "flex items-center gap-1 text-[10px] justify-center",
                                        subtaskStatus.color
                                    )}>
                                        <SubtaskStatusIcon className={cn("h-2.5 w-2.5", subtask.status === 'IN_PROGRESS' && "animate-spin")} />
                                    </div>

                                    {/* Empty cells for alignment */}
                                    <div />
                                    <div />
                                    <div />
                                    <div />
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            )}
        </>
    );
}

// Main landing page component
export function HomePage() {
    const { t, language, setLanguage } = useI18n();
    const { theme, setTheme } = useThemeStore();
    const [tasks, setTasks] = useState(demoTasks);
    const [selectedTask, setSelectedTask] = useState<string | null>(null);
    const [detailTask, setDetailTask] = useState<DemoTask | null>(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [expandedDemo, setExpandedDemo] = useState(false);
    const heroRef = useRef<HTMLDivElement>(null);

    const { scrollYProgress } = useScroll();
    const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
    const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

    // Mouse tracking for hero gradient
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (heroRef.current) {
                const rect = heroRef.current.getBoundingClientRect();
                setMousePosition({
                    x: ((e.clientX - rect.left) / rect.width) * 100,
                    y: ((e.clientY - rect.top) / rect.height) * 100,
                });
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const handleStatusChange = (id: string, status: DemoTask['status']) => {
        setTasks(prev => prev.map(t => t.id === id ? { ...t, status } : t));
        // Also update detail task if it's the one being changed
        if (detailTask && detailTask.id === id) {
            setDetailTask(prev => prev ? { ...prev, status } : null);
        }
    };

    const handleSubtaskStatusChange = (taskId: string, subtaskId: string, status: DemoTask['status']) => {
        setTasks(prev => prev.map(t => {
            if (t.id === taskId && t.subtasks) {
                return {
                    ...t,
                    subtasks: t.subtasks.map(st => st.id === subtaskId ? { ...st, status } : st),
                };
            }
            return t;
        }));
        // Also update detail task if it's the one being changed
        if (detailTask && detailTask.id === taskId && detailTask.subtasks) {
            setDetailTask(prev => {
                if (!prev || !prev.subtasks) return prev;
                return {
                    ...prev,
                    subtasks: prev.subtasks.map(st => st.id === subtaskId ? { ...st, status } : st),
                };
            });
        }
    };

    const handleTaskSelect = (task: DemoTask) => {
        setSelectedTask(task.id === selectedTask ? null : task.id);
        // In expanded view, clicking opens detail panel
        if (expandedDemo) {
            setDetailTask(task);
        }
    };

    const handleThemeSelect = (selectedTheme: Theme) => {
        setTheme(selectedTheme);
    };

    const features = [
        { icon: FolderKanban, title: t('landing.feature.projects'), desc: t('landing.feature.projectsDesc'), gradient: 'from-blue-500 to-cyan-500' },
        { icon: Users, title: t('landing.feature.team'), desc: t('landing.feature.teamDesc'), gradient: 'from-violet-500 to-purple-500' },
        { icon: Zap, title: t('landing.feature.realtime'), desc: t('landing.feature.realtimeDesc'), gradient: 'from-yellow-500 to-orange-500' },
        { icon: MessageSquare, title: t('landing.feature.comments'), desc: t('landing.feature.commentsDesc'), gradient: 'from-green-500 to-emerald-500' },
        { icon: LayoutGrid, title: t('landing.feature.views'), desc: t('landing.feature.viewsDesc'), gradient: 'from-pink-500 to-rose-500' },
        { icon: Palette, title: t('landing.feature.themes'), desc: t('landing.feature.themesDesc'), gradient: 'from-indigo-500 to-blue-500' },
        { icon: Bell, title: t('landing.feature.notifications'), desc: t('landing.feature.notificationsDesc'), gradient: 'from-red-500 to-pink-500' },
        { icon: Settings2, title: t('landing.feature.customFields'), desc: t('landing.feature.customFieldsDesc'), gradient: 'from-teal-500 to-cyan-500' },
    ];

    return (
        <div className="min-h-screen overflow-x-hidden">
            {/* Fixed Language Selector - Top Right - hide when detail panel is open */}
            {!detailTask && (
                <div className="fixed top-4 right-4 z-50">
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2"
                    >
                        {languageOptions.map((option) => (
                            <motion.button
                                key={option.value}
                                onClick={() => setLanguage(option.value)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={cn(
                                    "flex items-center gap-1.5 px-3 py-2 rounded-lg backdrop-blur-md border transition-all text-sm",
                                    language === option.value
                                        ? "bg-primary/20 border-primary/50 text-white"
                                        : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white"
                                )}
                            >
                                <span className="text-base">{option.flag}</span>
                                <span className="hidden sm:inline">{option.value.toUpperCase()}</span>
                            </motion.button>
                        ))}
                    </motion.div>
                </div>
            )}

            {/* Hero Section */}
            <motion.section
                ref={heroRef}
                style={{ opacity: heroOpacity, scale: heroScale }}
                className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden"
            >
                {/* Animated gradient background overlay */}
                <div
                    className="absolute inset-0 opacity-30 transition-all duration-1000 ease-out"
                    style={{
                        background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, var(--primary) 0%, transparent 50%)`,
                    }}
                />

                {/* Floating decorative elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(6)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute w-64 h-64 rounded-full"
                            style={{
                                background: `radial-gradient(circle, hsl(var(--primary) / 0.1) 0%, transparent 70%)`,
                                left: `${10 + i * 15}%`,
                                top: `${20 + (i % 3) * 25}%`,
                            }}
                            animate={{
                                y: [0, -30, 0],
                                scale: [1, 1.1, 1],
                                opacity: [0.3, 0.5, 0.3],
                            }}
                            transition={{
                                duration: 5 + i,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: i * 0.5,
                            }}
                        />
                    ))}
                </div>

                <div className="relative z-10 text-center max-w-5xl mx-auto">
                    {/* Logo / Brand */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-6"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md bg-white/5 border border-white/10">
                            <Gift className="h-4 w-4 text-primary" />
                            <span className="text-sm text-white/80">{t('landing.selfHosted')}</span>
                        </div>
                    </motion.div>

                    {/* Main headline */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-4 tracking-tight"
                    >
                        <span className="text-white">{t('landing.heroTitle')}</span>
                        <br />
                        <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                            {t('landing.heroSubtitle')}
                        </span>
                    </motion.h1>

                    {/* Tagline */}
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-lg sm:text-xl text-white/60 mb-8 max-w-2xl mx-auto"
                    >
                        {language === 'fr'
                            ? "Une platforme collaborative simple et intuitive pour organiser vos projets et travailler en équipe en temps réel."
                            : "A simple and intuitive collaborative platform to organize your projects and work with your team in real-time."}
                    </motion.p>

                    {/* CTA Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                    >
                        <Button asChild size="lg" className="group text-base px-8 py-6">
                            <Link to="/register">
                                {t('landing.getStarted')}
                                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Button>
                        <Button asChild variant="outline" size="lg" className="text-base px-8 py-6">
                            <Link to="/login">{t('auth.signIn')}</Link>
                        </Button>
                    </motion.div>
                </div>

                {/* Scroll indicator - positioned at bottom of hero */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 0.6 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2"
                >
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="flex flex-col items-center gap-2 text-white/40"
                    >
                        <span className="text-xs">{t('landing.scrollToExplore')}</span>
                        <ChevronDown className="h-5 w-5" />
                    </motion.div>
                </motion.div>
            </motion.section>

            {/* Features Section */}
            <section className="py-24 px-4">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            {language === 'fr' ? "Tout ce dont vous avez besoin" : "Everything you need"}
                        </h2>
                        <p className="text-white/60 max-w-2xl mx-auto">
                            {language === 'fr'
                                ? "Des fonctionnalités pensées pour gérer vos projets, collaborer avec votre équipe et rester organisé."
                                : "Features designed to manage your projects, collaborate with your team, and stay organized."}
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {features.map((feature, i) => (
                            <FeatureCard
                                key={feature.title}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.desc}
                                delay={i * 0.1}
                                gradient={feature.gradient}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Interactive Demo Section */}
            <section className="py-24 px-4 relative">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />

                <div className="max-w-6xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            {t('landing.demo.title')}
                        </h2>
                        <p className="text-white/60 max-w-2xl mx-auto">
                            {t('landing.demo.subtitle')}
                        </p>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Theme Selector */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-6"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                                    <Palette className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">{t('landing.demo.tryTheme')}</h3>
                                    <p className="text-sm text-white/50">
                                        {language === 'fr' ? "Le fond change instantanément !" : "Background changes instantly!"}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-3">
                                {themes.map((themeConfig) => (
                                    <ThemePreviewCard
                                        key={themeConfig.id}
                                        themeConfig={themeConfig}
                                        isSelected={theme === themeConfig.id}
                                        onClick={() => handleThemeSelect(themeConfig.id)}
                                        language={language}
                                    />
                                ))}
                            </div>

                            {/* Interactive effects info */}
                            <div className="mt-6 flex items-center gap-2 text-sm text-white/40">
                                <Sparkles className="h-4 w-4" />
                                <span>
                                    {language === 'fr'
                                        ? "Les thèmes avec ✨ ont des effets de particules animés"
                                        : "Themes with ✨ have animated particle effects"}
                                </span>
                            </div>
                        </motion.div>

                        {/* Task Demo */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                                        <FolderKanban className="h-4 w-4 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-white">{t('landing.demo.taskDemo')}</h3>
                                        <p className="text-xs text-white/50">{t('landing.demo.interactiveDemo')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="inProgress" className="text-[10px] px-1.5 py-0.5">
                                        {tasks.filter(t => t.status === 'IN_PROGRESS').length}
                                    </Badge>
                                    <Badge variant="done" className="text-[10px] px-1.5 py-0.5">
                                        {tasks.filter(t => t.status === 'DONE').length}
                                    </Badge>
                                </div>
                            </div>

                            <div className="max-h-72 overflow-y-auto">
                                <AnimatePresence>
                                    {tasks.map((task) => (
                                        <DemoTaskRow
                                            key={task.id}
                                            task={task}
                                            language={language}
                                            onStatusChange={handleStatusChange}
                                            isSelected={selectedTask === task.id}
                                            onSelect={() => handleTaskSelect(task)}
                                            isExpanded={false}
                                            showSubtasks={false}
                                        />
                                    ))}
                                </AnimatePresence>
                            </div>

                            <div className="px-4 py-2 border-t border-white/10 bg-white/5 flex items-center justify-between">
                                <p className="text-[10px] text-white/40">
                                    {language === 'fr'
                                        ? "💡 Cliquez sur le cercle pour changer le statut"
                                        : "💡 Click the circle to toggle status"}
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setExpandedDemo(true)}
                                    className="h-7 text-xs gap-1 text-white/60 hover:text-white"
                                >
                                    <Maximize2 className="h-3 w-3" />
                                    {language === 'fr' ? 'Agrandir' : 'Expand'}
                                </Button>
                            </div>
                        </motion.div>
                    </div>

                    {/* Expanded Demo Modal */}
                    <AnimatePresence>
                        {expandedDemo && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                                onClick={() => { setExpandedDemo(false); setDetailTask(null); }}
                            >
                                <motion.div
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    className="relative w-full max-w-5xl max-h-[85vh] backdrop-blur-md bg-surface/95 border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Modal Header */}
                                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                                                <FolderKanban className="h-5 w-5 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white">Demo Project</h3>
                                                <p className="text-sm text-white/50">{t('landing.demo.interactiveDemo')}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge variant="inProgress">
                                                {tasks.filter(t => t.status === 'IN_PROGRESS').length} {language === 'fr' ? 'en cours' : 'active'}
                                            </Badge>
                                            <Badge variant="done">
                                                {tasks.filter(t => t.status === 'DONE').length} {language === 'fr' ? 'terminées' : 'done'}
                                            </Badge>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => { setExpandedDemo(false); setDetailTask(null); }}
                                                className="h-8 w-8"
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Table Header - with fixed widths matching the grid */}
                                    <div className="grid grid-cols-[24px_1fr_100px_80px_90px_90px_80px] gap-3 px-3 py-2 border-b border-white/10 bg-white/5 text-xs text-white/50 font-medium items-center">
                                        <div />
                                        <div>{language === 'fr' ? 'Tâche' : 'Task'}</div>
                                        <div className="text-center">{language === 'fr' ? 'Statut' : 'Status'}</div>
                                        <div className="text-center">{language === 'fr' ? 'Priorité' : 'Priority'}</div>
                                        <div className="text-center">{language === 'fr' ? 'Assigné' : 'Assignee'}</div>
                                        <div className="text-center">{language === 'fr' ? 'Échéance' : 'Due'}</div>
                                        <div className="text-center"></div>
                                    </div>

                                    {/* Task List */}
                                    <div className="max-h-[60vh] overflow-y-auto">
                                        <AnimatePresence>
                                            {tasks.map((task) => (
                                                <DemoTaskRow
                                                    key={task.id}
                                                    task={task}
                                                    language={language}
                                                    onStatusChange={handleStatusChange}
                                                    onSubtaskStatusChange={handleSubtaskStatusChange}
                                                    isSelected={selectedTask === task.id}
                                                    onSelect={() => handleTaskSelect(task)}
                                                    isExpanded={true}
                                                    showSubtasks={true}
                                                />
                                            ))}
                                        </AnimatePresence>
                                    </div>

                                    {/* Modal Footer */}
                                    <div className="px-6 py-3 border-t border-white/10 bg-white/5 flex items-center justify-between">
                                        <p className="text-xs text-white/40">
                                            {language === 'fr'
                                                ? "💡 Cliquez sur une tâche pour voir les détails, commentaires et pièces jointes"
                                                : "💡 Click a task to view details, comments and attachments"}
                                        </p>
                                        <Button asChild size="sm">
                                            <Link to="/register">
                                                {t('landing.getStarted')}
                                                <ArrowRight className="ml-2 h-3 w-3" />
                                            </Link>
                                        </Button>
                                    </div>
                                </motion.div>

                                {/* Task Detail Panel - slides in from right */}
                                <AnimatePresence>
                                    {detailTask && (
                                        <DemoTaskDetailPanel
                                            task={detailTask}
                                            language={language}
                                            onClose={() => setDetailTask(null)}
                                            onStatusChange={(status) => handleStatusChange(detailTask.id, status)}
                                            onSubtaskStatusChange={(subtaskId, status) => handleSubtaskStatusChange(detailTask.id, subtaskId, status)}
                                        />
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="grid grid-cols-2 md:grid-cols-3 gap-12"
                    >
                        {[
                            { value: 8, suffix: '+', label: language === 'fr' ? 'Thèmes' : 'Themes' },
                            { value: 0, suffix: '€', label: language === 'fr' ? 'Pour toujours' : 'Forever' },
                            { value: 24, suffix: '/7', label: language === 'fr' ? 'Vos données' : 'Your data' },
                        ].map((stat, i) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="text-center"
                            >
                                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                                    <AnimatedCounter target={stat.value} />
                                    {stat.suffix}
                                </div>
                                <div className="text-white/50 text-sm mt-2">{stat.label}</div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Free for everyone callout */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="relative backdrop-blur-md bg-gradient-to-r from-primary/10 to-accent/10 border border-white/10 rounded-3xl p-8 md:p-12 overflow-hidden"
                    >
                        {/* Decorative elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

                        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                            <div className="flex-shrink-0">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                                    <Gift className="h-10 w-10 text-white" />
                                </div>
                            </div>
                            <div className="text-center md:text-left">
                                <h3 className="text-2xl font-bold text-white mb-2">
                                    {t('landing.freeForAll')}
                                </h3>
                                <p className="text-white/60 mb-4">
                                    {t('landing.freeForAllDesc')}
                                </p>
                                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                                    <Badge variant="secondary" className="gap-1">
                                        <Gift className="h-3 w-3 text-green-400" />
                                        {language === 'fr' ? 'Gratuit' : 'Free'}
                                    </Badge>
                                    <Badge variant="secondary" className="gap-1">
                                        <Cloud className="h-3 w-3 text-blue-400" />
                                        {language === 'fr' ? 'Hébergé' : 'Hosted'}
                                    </Badge>
                                    <Badge variant="secondary" className="gap-1">
                                        <Zap className="h-3 w-3 text-yellow-400" />
                                        {language === 'fr' ? 'Rapide' : 'Fast'}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-24 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                            {t('landing.cta.title')}
                        </h2>
                        <p className="text-lg text-white/60 mb-8">
                            {t('landing.cta.subtitle')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button asChild size="lg" className="group text-base px-8 py-6">
                                <Link to="/register">
                                    {t('landing.getStarted')}
                                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg" className="text-base px-8 py-6">
                                <Link to="/login">{t('auth.signIn')}</Link>
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-white/10 py-8 px-4">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-white/40 text-sm">
                        <span>{t('landing.builtWith')}</span>
                        <Heart className="h-4 w-4 text-red-500 fill-red-500" />
                        <span>&</span>
                        <span className="text-white/60">React, Fastify, PostgreSQL</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-white/40 text-sm">Project Manager © 2026</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
